import {Logger} from '@vanti/vue-logger';
import DeferUtil from '@/store/defer-util';
import {createSnippet, decorateSnapshot} from '@/api/firebase';
import {LoadingUtil} from '@/utils/loading';
import {addTime, currentDate, minutes, toDate, toTime} from '@/utils/dates';
import {addDays, startOfDay} from 'date-fns';
import firebase from 'firebase/app';
import {byRef, indexQuerySnapshotUpdates, updateDataToArray} from '@/utils/firestore';
import {scheduleTicks} from '@/utils/schedule';
import {collectValues} from '@/utils/collect';
import {hasCheckIn, requiresCheckIn, reservedToProp, scheduleProps} from '@/room-booking/v1/bookings';
import * as bookings from '@/room-booking/v1/bookings';
import * as bookingsUtil from '@/utils/bookings-v1';
import {RoomState} from '@/api/consts';

const log = Logger.get('hybrid-office');

/**
 * The store module for the hybrid-office feature.
 *
 * The panel in this mode is effectively for a desk. The store includes the bookable document for the desk,
 * as well as associated bookings in the `bookings` sub-module.
 */
export default {
  namespaced: true,
  state: {
    desk: null,
    ...LoadingUtil.state()
  },
  getters: {
    ...LoadingUtil.getters(log),
    bookable(state, getters, rootState, rootGetters) {
      return rootGetters['config/hybridOfficeBookable'];
    },
    enabled(state, getters) {
      return Boolean(getters['bookable']);
    },
    title(state, getters) {
      if (getters.bookable && getters.bookable.title) {
        return getters.bookable.title;
      }
      return state.desk && state.desk.title;
    },
    roomState(state, getters) {
      const booking = getters['bookings/currentBooking'];
      if (!booking) {
        return RoomState.AVAILABLE;
      }
      if (!requiresCheckIn(booking, '') || hasCheckIn(booking, '')) {
        return RoomState.OCCUPIED;
      }
      return RoomState.BOOKED;
    }
  },
  mutations: {
    ...DeferUtil.mutations(log),
    ...LoadingUtil.mutations(),
    setDesk(state, desk) {
      state.desk = desk;
    },
    clear(state) {
      state.desk = null;
    }
  },
  actions: {
    init: {
      root: true,
      handler({commit, getters, dispatch}) {
        this.watch(
            () => [
              getters['enabled'],
              getters['bookable']
            ],
            ([enabled, bookable]) => {
              log.debug('change', {enabled, bookable});

              if (enabled && bookable) {
                dispatch('bindDesk', bookable);
                dispatch('bookings/bindBookings', {nowMs: Date.now()})
                    .catch(err => log.error('During bindBookings', err));

                // set up daily query refresh
                dispatch('bookings/scheduleBookingRefresh')
                    .catch(err => log.error('During scheduleBookingRefresh', err));

                dispatch('bookings/bindChangeSchedule')
                    .catch(err => log.error('During bindChangeSchedule', err));
              } else {
                commit('bookings/reset');
                commit('bookings/clear');
                commit('reset');
                commit('clear');
              }
            }
        );
      }
    },
    bindDesk({commit}, snip) {
      log.debug('bindDesk', snip);
      commit('loading', 'desk');
      const defer = {};
      defer.desk = snip.ref.onSnapshot(
          snap => {
            commit('setDesk', decorateSnapshot(snap));
            commit('loaded', 'desk');
          },
          err => {
            log.error(snip.ref.path + 'onSnapshot', err);
            commit('loaded', 'desk');
          }
      );
      commit('defer', defer);
    }
  },
  modules: {
    bookings: {
      namespaced: true,
      state: {
        /**
         * Bookings by ref
         */
        byRef: {},
        /**
         * Represents the most recent 'event' relative to bookings. This is typically a start or end time for known
         * bookings. See changeSchedule for how all the 'ticks' on the timeline are calculated.
         *
         * @type {CanBeDate}
         */
        time: currentDate().toISOString(),
        /**
         * The time around which we fetch bookings for
         *
         * @type {CanBeDate}
         */
        queryTime: currentDate().toISOString()
      },
      getters: {
        now(state) {
          return toDate(state.time);
        },
        bookings(state) {
          return Object.values(state.byRef);
        },
        closestBooking(state, getters) {
          return bookingsUtil.closestBooking(getters.bookings, () => '', getters.now);
        },
        /**
         * The current (now) booking is one that intersects the current time
         *
         * @param {Object} state
         * @param {Object} getters
         * @return {Booking|null}
         */
        currentBooking(state, getters) {
          const now = getters.now;
          /** @type {Booking|null} */
          const b = getters.closestBooking;
          if (!b) return null;

          if (bookingsUtil.isActive(b, '', now)) {
            return b;
          }
          return null;
        },
        nextBooking(state, getters) {
          const last = getters.currentBooking;
          if (!last) return bookingsUtil.nextBooking(getters.bookings, () => '', getters.now);
          return bookingsUtil.nextBooking(getters.bookings, () => '',
              bookingsUtil.bookingEnd(last, ''));
        },
        /**
         * Returns a sorted (ascending) array of all the fromTime and toTimes for all known bookings. This is used to
         * calculate when we need to 'wake up' to change our state to correctly represent the active bookings.
         * @param {Object} state
         * @return {Date[]}
         */
        changeSchedule(state) {
          // impl notes:
          // 1: Date doesn't play nicely with Set, it appears to be compared based on object identity, so we use getTime
          // 2: Numbers aren't sorted correctly in arrays as they are 'converted to strings' before sorting
          const changes = new Set();
          Object.values(state.byRef).forEach(b => {
            const times = collectValues(b, ...scheduleProps(''));
            times.map(v => toTime(v)).forEach(t => changes.add(t));
          });
          return Array.from(changes).sort((a, b) => a - b).map(t => new Date(t));
        }
      },
      mutations: {
        ...DeferUtil.mutations(log),
        setTime(state, time) {
          state.time = time.toISOString();
        },
        applyBookingSnapshotUpdate(state, snap) {
          indexQuerySnapshotUpdates(state.byRef, snap, byRef);
        },
        clear(state) {
          state.byRef = {};
        }
      },
      actions: {
        bindBookings({commit, rootGetters}, {nowMs}) {
          const bookable = rootGetters['hybridOffice/bookable'];

          const now = new Date(nowMs);
          const startOfToday = startOfDay(now);
          const startOfTomorrow = startOfDay(addDays(now, 1));
          const startAfter = firebase.firestore.Timestamp.fromDate(startOfToday);
          const startBefore = firebase.firestore.Timestamp.fromDate(startOfTomorrow);

          // ns/{ns}/sites/{site}/bookables/{bookable} => ns/{ns}/sites/{site}/bookings
          /** @type {firebase.firestore.CollectionReference} */
          const bookings = bookable.ref.parent.parent.collection('bookings');

          log.debug(`bindBookings`, {
            bookable: bookable.ref.path,
            bookings: bookings.path,
            startAfter: startOfToday,
            startBefore: startOfTomorrow
          });

          const query = bookings
              .where('space.ref', '==', bookable.ref)
              .where('fromTime', '>=', startAfter)
              .where('fromTime', '<', startBefore)
              .orderBy('fromTime');

          const defer = {};
          defer.bookings = query.onSnapshot(
              snap => commit('applyBookingSnapshotUpdate', snap),
              err => log.error('bookings.onSnapshot', err)
          );
          commit('defer', defer);
        },
        /**
         * Call bindBookings each day at midnight
         *
         * @param {*} context
         * @return {Promise<void>}
         */
        async scheduleBookingRefresh({commit, dispatch}) {
          const defer = {};
          const startOfTomorrow = addDays(startOfDay(new Date()), 1);
          const msTilStartOfTomorrow = startOfTomorrow.getTime() - Date.now();
          log.debug(`scheduleBookingRefresh in ${msTilStartOfTomorrow}ms`);

          // call bindBookings at midnight
          const nextRefreshHandle = setTimeout(() => {
            dispatch('bindBookings', {nowMs: Date.now()})
                .catch(err => log.error('During bindBookings refresh', err));
            // set up the next refresh
            dispatch('scheduleBookingRefresh')
                .catch(err => log.error('During scheduleBookingRefresh setup', err));
          }, msTilStartOfTomorrow);
          defer.nextRefreshHandle = () => clearTimeout(nextRefreshHandle);

          commit('defer', defer);
        },
        bindChangeSchedule({getters, commit, dispatch}) {
          commit('defer', {
            changeSchedule: this.watch(
                () => getters.changeSchedule,
                changeSchedule => {
                  const now = new Date();
                  dispatch('scheduleTimeChanges', now);
                  if (changeSchedule.every(t => t < now) && changeSchedule.length > 0) {
                    // all ticks in the past, make sure time is the latest change
                    const latest = changeSchedule[changeSchedule.length - 1];
                    if (getters.now < latest) {
                      commit('setTime', latest);
                    }
                  }
                },
                {immediate: true}
            )
          });
        },
        scheduleTimeChanges({getters, commit}, now) {
          commit('defer', {
            scheduleTimeChanges: scheduleTicks(getters.changeSchedule, d => commit('setTime', d))
          });
        },

        async createAdHocBooking({rootGetters, rootState}, mins) {
          const bookable = rootState.hybridOffice.desk;
          if (!bookable) throw new Error('no active bookable');
          const createable = rootGetters['createableSnippet'];
          const fromTime = currentDate();
          const toTime = addTime(fromTime, mins * minutes);
          const booking = bookings.newAdHocBooking(fromTime, toTime, createSnippet(bookable), createable);

          /** @type {firebase.firestore.CollectionReference} */
          const bookingsCol = bookable.ref.parent.parent.collection('bookings');
          return bookingsCol.add(booking);
        },

        /**
         * Release (check-out) a booking.
         * A function is returned to undo the action
         *
         * @param {Object} [context]
         * @param {DecoratedData} booking
         * @return {Promise<function()>}
         */
        async release({getters}, {booking}) {
          // edit end time to now
          const update = {};
          const undo = {};
          const toTime = booking.toTime;
          update.toTime = currentDate();
          undo.toTime = toTime;
          await booking.ref.update(...updateDataToArray(update));

          // return an undo action
          return async () => {
            await booking.ref.update(...updateDataToArray(undo));
          };
        },

        /**
         * Extend the end time of a booking
         * A function is returned to undo the action
         *
         * @param {object} [context]
         * @param {object} payload
         * @param {Booking} payload.booking
         * @param {number} payload.mins
         * @return {Promise<*>}
         */
        async extendBooking({dispatch, getters}, {booking, mins}) {
          // update reservation reservedPeriod.toTime
          const startTime = toDate(bookings.toTime(booking));
          const prop = reservedToProp('');
          await booking.ref.update({
            [prop]: addTime(startTime, mins * minutes)
          });

          // return the undo action
          return async () => {
            await booking.ref.update({
              [prop]: startTime
            });
          };
        }
      }
    }
  }
};
